import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import salonService from "../services/SalonService";
import { useSalon } from "./SalonContext";

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
    const { salonUuid } = useSalon();
    const [themeData, setThemeData] = useState(null);
    const [isDarkMode, setIsDarkMode] = useState(false);

    const applyTheme = useCallback((theme) => {
        if (!theme) return;

        const root = document.documentElement;
        const currentTheme = isDarkMode ? theme.dark : theme.light;

        // Map API DTO fields to CSS Variables
        const variableMap = {
            '--primary-color': currentTheme.primary,
            '--secondary-color': currentTheme.secondary,
            '--background-color': currentTheme.background,
            '--card-bg': currentTheme.card,
            '--surface-color': currentTheme.surface,
            '--text-primary': currentTheme.textPrimary,
            '--text-secondary': currentTheme.textSecondary,
            '--text-third': currentTheme.textThird,
            '--text-on-primary': currentTheme.textOnPrimary,
            '--icon-primary': currentTheme.iconPrimary,
            '--icon-secondary': currentTheme.iconSecondary,
            '--button-primary': currentTheme.buttonPrimary,
            '--button-primary-text': currentTheme.buttonPrimaryText,
            '--button-disabled': currentTheme.buttonDisabled,
            '--slot-available': currentTheme.slotAvailable,
        };

        Object.entries(variableMap).forEach(([key, value]) => {
            if (value) root.style.setProperty(key, value);
        });

        // Handle Header Gradient
        if (currentTheme.headerGradient && currentTheme.headerGradient.length >= 2) {
            root.style.setProperty('--header-gradient', `linear-gradient(135deg, ${currentTheme.headerGradient[0]} 0%, ${currentTheme.headerGradient[1]} 100%)`);
        }
    }, [isDarkMode]);

    useEffect(() => {
        const fetchTheme = async () => {
            const mySalonUuid = salonUuid || localStorage.getItem("SALON_UUID");
            if (mySalonUuid) {
                try {
                    const response = await salonService.getAppTheme(mySalonUuid);
                    setThemeData(response.data);
                    applyTheme(response.data);
                } catch (error) {
                    console.error("Failed to fetch theme", error);
                }
            }
        };

        fetchTheme();
    }, [salonUuid, applyTheme]);

    // Re-apply theme if dark mode changes
    useEffect(() => {
        if (themeData) {
            applyTheme(themeData);
        }
    }, [isDarkMode, themeData, applyTheme]);

    const toggleDarkMode = () => setIsDarkMode(!isDarkMode);

    return (
        <ThemeContext.Provider value={{ themeData, isDarkMode, toggleDarkMode }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useAppTheme = () => useContext(ThemeContext);
